import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bell, CheckCircle2, Clock, Phone, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export const FollowUpsPage = () => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');

  useEffect(() => {
    loadFollowUps();
  }, [filterType, filterStatus]);

  const loadFollowUps = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      
      const response = await api.get('/follow-ups', { params });
      setFollowUps(response.data);
    } catch (error) {
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await api.patch(`/follow-ups/${id}/complete`);
      toast.success('Follow-up marked as completed!');
      loadFollowUps();
    } catch (error) {
      toast.error('Failed to update follow-up');
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'call': return <Phone className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'timesheet': return 'bg-blue-100 text-blue-800';
      case 'payment': return 'bg-green-100 text-green-800';
      case 'po': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isOverdue = (scheduledDate) => {
    return new Date(scheduledDate) < new Date();
  };

  const pendingCount = followUps.filter(f => f.status === 'pending').length;
  const overdueCount = followUps.filter(f => f.status === 'pending' && isOverdue(f.scheduled_date)).length;
  const completedCount = followUps.filter(f => f.status === 'completed').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="followups-title">Automated Follow-ups</h1>
          <p className="text-muted-foreground mt-1">{followUps.length} total follow-ups</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Pending</div>
          <div className="text-3xl font-bold text-amber-600" data-testid="pending-count">{pendingCount}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Overdue</div>
          <div className="text-3xl font-bold text-red-600" data-testid="overdue-count">{overdueCount}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Completed</div>
          <div className="text-3xl font-bold text-green-600" data-testid="completed-count">{completedCount}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total</div>
          <div className="text-3xl font-bold">{followUps.length}</div>
        </Card>
      </div>

      {/* Follow-up Schedule Info */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-bold text-lg mb-3">Automated Follow-up Schedules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="font-semibold text-blue-900 mb-2">Timesheet Reminders:</div>
            <ul className="space-y-1 text-blue-700">
              <li>• 1st of month: First reminder</li>
              <li>• 5th: Second reminder (+ Sales person)</li>
              <li>• 10th: Third reminder</li>
              <li>• 11th-14th: Daily email reminders</li>
              <li>• 15th+: Daily email + automated calls</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-blue-900 mb-2">Payment Reminders:</div>
            <ul className="space-y-1 text-blue-700">
              <li>• Week 1-4: Weekly email reminders</li>
              <li>• Day 30+: Daily email reminders</li>
              <li>• Day 30+: Automated call reminders</li>
              <li>• Full escalation to management</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="text-xs text-muted-foreground">Type</label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px]" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="timesheet">Timesheet</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
                <SelectItem value="po">Purchase Order</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Status</label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Follow-ups List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading follow-ups...</div>
        </div>
      ) : followUps.length === 0 ? (
        <Card className="p-12 text-center">
          <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No follow-ups scheduled</h3>
          <p className="text-muted-foreground">Follow-ups are auto-generated based on timesheets and payments</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {followUps.map((followUp) => {
            const overdue = isOverdue(followUp.scheduled_date) && followUp.status === 'pending';
            
            return (
              <Card key={followUp.id} className={`p-4 ${overdue ? 'border-red-300 bg-red-50' : ''}`} data-testid={`followup-card-${followUp.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center gap-2">
                      {getChannelIcon(followUp.channel)}
                      <Badge className={getTypeColor(followUp.type)}>
                        {followUp.type.toUpperCase()}
                      </Badge>
                    </div>
                    
                    <div className="flex-1">
                      <div className="font-semibold">
                        {followUp.type === 'timesheet' && 'Timesheet Reminder'}
                        {followUp.type === 'payment' && 'Payment Reminder'}
                        {followUp.type === 'po' && 'Purchase Order Follow-up'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Scheduled: {new Date(followUp.scheduled_date).toLocaleDateString()} via {followUp.channel}
                        {overdue && <span className="ml-2 text-red-600 font-semibold">OVERDUE</span>}
                      </div>
                      {followUp.notes && (
                        <div className="text-xs text-muted-foreground mt-1">{followUp.notes}</div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {followUp.status === 'pending' ? (
                        <>
                          <Badge className="bg-amber-100 text-amber-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleComplete(followUp.id)}
                            data-testid={`complete-${followUp.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Mark Sent
                          </Button>
                        </>
                      ) : (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Completed
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}\n        </div>
      )}

      {/* Info Box */}
      <Card className="p-4 bg-gray-50">
        <div className="text-sm text-muted-foreground">
          <strong>Note:</strong> Follow-ups are currently tracked manually. Full automation with email/SMS/call integration will be available in the next update. 
          For now, use this page to track and manage follow-up schedules.
        </div>
      </Card>
    </div>
  );
};
